import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react';
import Button from '../components/Button';
const CaseStudyPage: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  // This would typically come from a database or API
  const caseStudies = [{
    id: '1',
    title: 'User Retention Optimization',
    subtitle: 'Increasing retention by 40% and driving $2M in ARR',
    company: 'Florida Blue',
    duration: '6 months',
    role: 'Product Lead',
    challenge: 'Florida Blue was experiencing high user churn rates, with only 35% of new users remaining active after 30 days. This was significantly impacting revenue growth and increasing customer acquisition costs.',
    approach: ['Conducted extensive user research to identify key pain points in the onboarding process', 'Analyzed user behavior data to identify patterns of both churned and retained users', 'Developed a personalized onboarding experience based on user segments and use cases', 'Implemented an engagement strategy with targeted touchpoints throughout the user journey', 'Leveraged AI to provide personalized recommendations and content to users'],
    solution: 'We redesigned the onboarding experience to focus on key value moments and implemented a personalized engagement strategy. The new onboarding flow guided users through setting up their account based on their specific needs and use cases, ensuring they experienced value quickly. We also developed an AI-powered recommendation system that suggested relevant features and content based on user behavior.',
    results: ['Increased 30-day user retention by 40%', 'Drove $2M in additional Annual Recurring Revenue', 'Reduced customer acquisition cost by 25%', 'Improved user satisfaction scores by 18 points', 'Increased feature adoption by 35%'],
    learnings: 'This project reinforced the importance of understanding user needs and pain points before implementing solutions. We discovered that different user segments had vastly different needs and expectations, requiring a more personalized approach. The most valuable insight was that showing users immediate value and guiding them to their specific "aha moment" as quickly as possible had the biggest impact on long-term retention.',
    technologies: ['User Analytics', 'A/B Testing', 'Machine Learning', 'Customer Segmentation'],
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    images: ['https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1551434908-b3e7d4c5e0c6?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1551434979-2769a6e1b31d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80']
  }, {
    id: '2',
    title: 'Sales Compensation Process Optimization',
    subtitle: 'Reducing overpayment by $1.2M and payment time by 15 days',
    company: 'Florida Blue',
    duration: '8 months',
    role: 'Product Manager',
    challenge: 'Florida Blue was struggling with payment processing inefficiencies that resulted in frequent overpayments and delayed processing times. This was causing financial losses and negatively impacting vendor relationships.',
    approach: ['Mapped the existing payment workflow to identify bottlenecks and inefficiencies', 'Collaborated with finance and operations teams to understand requirements and constraints', 'Developed a new payment validation system with automated checks and balances', 'Implemented a digital approval workflow to replace manual processes', 'Created a dashboard for real-time visibility into payment status and history'],
    solution: 'We redesigned the payment workflow with a focus on automation and validation. The new system included automated validation checks to prevent overpayments and duplicate payments. We also implemented a digital approval workflow that replaced manual paper-based processes, significantly reducing processing time. A real-time dashboard provided stakeholders with visibility into payment status and history.',
    results: ['Reduced overpayment by $1.2M annually', 'Decreased payment processing time by 15 days', 'Improved payment accuracy by 35%', 'Reduced manual processing time by 60%', 'Increased vendor satisfaction scores by 25%'],
    learnings: "This project highlighted the importance of cross-functional collaboration and stakeholder alignment. We found that involving end-users in the design process led to higher adoption rates and better outcomes. The most significant learning was that automation alone wasn't enough; we needed to redesign the entire process to eliminate bottlenecks and redundancies.",
    technologies: 'Workflow Automation, Process Mining, Business Intelligence, Digital Signatures.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    images: ['https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1556740714-a8395b3bf30f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80']
  }, {
    id: '3',
    title: 'Customer Satisfaction Enhancement',
    subtitle: 'Increasing CSAT by 30% and NPS by 15 points',
    company: 'CVS Health',
    duration: '5 months',
    role: 'Associate Product Manager',
    challenge: 'CVS Health was receiving negative feedback about their customer support experience, with low CSAT scores and a high volume of support tickets. This was impacting customer retention and brand reputation.',
    approach: ['Implemented a comprehensive voice-of-customer program to gather feedback', 'Analyzed support ticket data to identify common issues and pain points', 'Collaborated with the support team to redesign the support experience', 'Developed an AI-powered recommendation system to provide self-service solutions', 'Created a customer feedback loop to continuously improve the experience'],
    solution: 'We redesigned the customer support experience with a focus on self-service and personalization. The new system included an AI-powered knowledge base that provided contextual recommendations based on user queries. We also implemented a streamlined ticket routing system that ensured customers were connected with the right specialist quickly. A continuous feedback loop allowed us to identify and address issues proactively.',
    results: ['Increased Customer Satisfaction (CSAT) by 30%', 'Improved Net Promoter Score (NPS) by 15 points', 'Reduced support ticket volume by 25%', 'Decreased average resolution time by 40%', 'Increased self-service resolution rate by 60%'],
    learnings: 'This project demonstrated the power of listening to customer feedback and taking action. We learned that customers value being heard as much as they value quick resolutions. The most important insight was that empowering customers with self-service tools not only improved satisfaction but also reduced support costs.',
    technologies: ['AI Recommendations', 'Natural Language Processing', 'Customer Feedback Analysis', 'Knowledge Management'],
    image: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    images: ['https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80', 'https://images.unsplash.com/photo-1552664688-cf412ec27db2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80']
  }];
  const caseStudy = caseStudies.find(study => study.id === id);
  if (!caseStudy) {
    return <div className="max-w-7xl mx-auto px-4 sm-px-6 lg-px-8 py-16." data-id="element-226">
        <div className="text-center" data-id="element-227">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-228">
            Case Study Not Found
          </h2>
          <p className="mt-4 text-gray-600" data-id="element-229">
            The case study you're looking for doesn't exist or has been removed.
          </p>
          <div className="mt-8" data-id="element-230">
            <Button href="/" variant="primary" data-id="element-231">
              Return to Home
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-slate-50 w-full" data-id="element-232">
      {/* Hero Section */}
      <section className="bg-white" data-id="element-233">
        <div className="max-w-7xl mx-auto px-4 sm-px-6 lg-px-8 py-16." data-id="element-234">
          <div className="mb-8" data-id="element-235">
            <Link to="/#case-studies" className="inline-flex items-center text-blue-600 hover:text-blue-800" data-id="element-236">
              <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-237" /> Back to Case Studies
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-238">
            <div data-id="element-239">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-240">
                {caseStudy.title}
              </h1>
              <p className="mt-4 text-xl text-gray-600" data-id="element-241">{caseStudy.subtitle}</p>
              <div className="mt-8 grid grid-cols-2 gap-6" data-id="element-242">
                <div data-id="element-243">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-244">Company</h3>
                  <p className="mt-1 text-base font-medium text-gray-900" data-id="element-245">
                    {caseStudy.company}
                  </p>
                </div>
                <div data-id="element-246">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-247">
                    Duration
                  </h3>
                  <p className="mt-1 text-base font-medium text-gray-900" data-id="element-248">
                    {caseStudy.duration}
                  </p>
                </div>
                <div data-id="element-249">
                  <h3 className="text-sm font-medium text-gray-500" data-id="element-250">Role</h3>
                  <p className="mt-1 text-base font-medium text-gray-900" data-id="element-251">
                    {caseStudy.role}
                  </p>
                </div>
              </div>
            </div>
            <div data-id="element-252">
              <img src={caseStudy.image} alt={caseStudy.title} className="rounded-lg shadow-lg w-full" data-id="element-253" />
            </div>
          </div>
        </div>
      </section>
      {/* Challenge Section */}
      <section className="py-16 bg-slate-50" data-id="element-254">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-255">
          <div className="max-w-3xl mx-auto" data-id="element-256">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-257">
              The Challenge
            </h2>
            <p className="text-lg text-gray-600" data-id="element-258">{caseStudy.challenge}</p>
          </div>
        </div>
      </section>
      {/* Approach Section */}
      <section className="py-16 bg-white" data-id="element-259">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-260">
          <div className="max-w-3xl mx-auto" data-id="element-261">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-262">
              My Approach
            </h2>
            <ol className="space-y-4" data-id="element-263">
              {caseStudy.approach.map((step, index) => <li key={index} className="flex" data-id="element-264">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3 mt-0.5" data-id="element-265">
                    {index + 1}
                  </div>
                  <span className="text-gray-600" data-id="element-266">{step}</span>
                </li>)}
            </ol>
          </div>
        </div>
      </section>
      {/* Solution Section */}
      <section className="py-16 bg-slate-50" data-id="element-267">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-268">
          <div className="max-w-3xl mx-auto" data-id="element-269">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-270">
              The Solution
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="element-271">{caseStudy.solution}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-272">
              {caseStudy.images.map((image, index) => <div key={index} className="overflow-hidden rounded-lg shadow-md" data-id="element-273">
                  <img src={image} alt={`${caseStudy.title} - Image ${index + 1}`} className="w-full h-48 object-cover" data-id="element-274" />
                </div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Results Section */}
      <section className="py-16 bg-primary-700 text-white" data-id="element-275">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
          <div className="max-w-3xl mx-auto" data-id="element-277">
            <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-278">
              Results & Impact
            </h2>
            <div className="bg-white rounded-lg shadow-lg p-6" data-id="element-279">
              <ul className="space-y-4" data-id="element-280">
                {caseStudy.results.map((result, index) => <li key={index} className="flex items-center" data-id="element-281">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 mr-3" data-id="element-282">
                      <ChevronRightIcon className="h-5 w-5" data-id="element-283" />
                    </div>
                    <span className="text-secondary-900 font-medium" data-id="element-284">
                      {result}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Learnings Section */}
      <section className="py-16 bg-white" data-id="element-285">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
          <div className="max-w-3xl mx-auto" data-id="element-287">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-288">
              Key Learnings
            </h2>
            <p className="text-lg text-gray-600" data-id="element-289">{caseStudy.learnings}</p>
            <div className="mt-8" data-id="element-290">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-291">
                Technologies & Methods Used
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-292">
                {caseStudy.technologies.map((tech, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium" data-id="element-293">
                    {tech}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Next Case Study Section */}
      <section className="py-16 bg-slate-50" data-id="element-294">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-295">
          <div className="text-center" data-id="element-296">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-297">
              Explore More Case Studies
            </h2>
            <div className="flex justify-center space-x-4" data-id="element-298">
              {caseStudies.filter(study => study.id !== id).slice(0, 2).map(study => <Button key={study.id} href={`/case-study/${study.id}`} variant={study.id === '1' ? 'primary' : 'outline'} data-id="element-299">
                    {study.title}
                  </Button>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default CaseStudyPage;